import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { map, isEmpty, filter, isEqual } from "lodash";
import cn from "classnames";

import CartItem from "./CartItem";

import { ReactComponent as CartIcon } from "../assets/icons/EmptyCart.svg";

import styles from "./CartItems.module.scss";
import useOfflineModal from "../hooks/useOfflineModal";
import { sendCFDScreenNav, sendUpdateCart } from "../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend";
import { useSelector } from "react-redux";
import { getDeviceMode } from "../VNMode/Selectors";
import { MODES } from "../VNMode/Reducer";
import { CFD_SCREEN } from "../VNCustomerFacingDisplay/Reducer";

const CartItems = ({
  cartItems,
  onRemove,
  onSelect,
  newItemFocus,
  clearFocus,
  addPromotionsToOrder,
  history,
  className,
  discountsAvailable,
  networkAvailable,
  discounts,
  clearPromotions
}) => {
  const classNames = cn(styles.cartItems, className);
  let emptyElement = null;
  const [checkIfOfflineWithCallback, offlineModal] = useOfflineModal()
  const cfdMode = useSelector(state => getDeviceMode(state))
  
  const [localCartItems, setLocalCartItems] = useState({})
  const [localDiscounts, setLocalDiscounts] = useState({})

  const updateTheCartWithTheLatest = () => {
     // save it
     setLocalCartItems(cartItems)
     setLocalDiscounts(discounts)

     const mappedDiscounts = discounts.map((discount) => {
       discount.type = 'discount'
       return discount
     })

     sendUpdateCart([...cartItems, ...mappedDiscounts])
  }

  useEffect(()=> {
    if (isEmpty(cartItems)) {
      clearPromotions()
    }
  }, [cartItems.length])

  // TODO: (PLOETZ) - Prevent this component from constantly re-rendering (one of offenders is NetworkIndicator.js)
  useEffect(() => {
    if (cfdMode === MODES.POS) {

      // we only care about when the cart items actually update
      if (!isEqual(localCartItems, cartItems) || !isEqual(localDiscounts, discounts)) {
        
        // if this is ever called, usually on initialization with nothing, we don't need to do anything
        if (isEmpty(cartItems) && isEmpty(localCartItems)) {
          return
        }

        // going from something in the cart to empty
        if (isEmpty(cartItems) && !isEmpty(localCartItems)) {
          sendCFDScreenNav(CFD_SCREEN.IDLE)
          updateTheCartWithTheLatest()
          return
        }

        // we have something new in the cart, go to the cart screen and update the data
        if (!isEqual(localCartItems, cartItems)) {
          sendCFDScreenNav(CFD_SCREEN.CART)
          updateTheCartWithTheLatest()
          return
        }

        // the discounts updated, update the cart, but don't navigate
        if (!isEqual(localDiscounts, discounts)) {
          updateTheCartWithTheLatest()
          return
        }
      }
    }
  }, [cartItems, discounts])

  if (isEmpty(cartItems)) {
    return (
      <div className={classNames}>
        <div className={styles.empty}>
          <CartIcon />
          <p>Your cart is empty</p>
        </div>
      </div>
    )
  }

  return (
    <div className={classNames}>
      {emptyElement}
      {map(cartItems, (cartItem, index, items) => (
        <CartItem
          key={`${cartItem.id}-${index}`}
          {...cartItem}
          modifiers={cartItem.modifiersString}
          onRemove={() => onRemove(index)}
          onSelect={() => onSelect(index)}
          className={styles.cartItem}
          focusItem={newItemFocus && index === items.length - 1}
          clearFocus={clearFocus}
        />
      ))}

      {map(discounts, (discount, index, discounts) => (
        <CartItem
          key={`${discount.uuid}-${index}`}
          onRemove={() => {
            const selectedDiscounts = filter(
              discounts,
              (selectedDiscount) => selectedDiscount.uuid !== discount.uuid
            );
            addPromotionsToOrder(selectedDiscounts);
          }}
          className={styles.cartItem}
          isDiscount={true}
          {...discount}
        />
      ))}
      {discountsAvailable && (
        <div className={styles.addDiscount}>
          Discounts
          <button
            className={styles.addButton}
            onClick={() => {
              checkIfOfflineWithCallback(() => history.push("/concession-order/select-discount"), 'Discounts are not supported while the\ndevice is offline.', false)
            }}
          >
            Add ＋
          </button>
        </div>
      )}
      {offlineModal}
    </div>
  );
};

CartItems.propTypes = {
  cartItems: [],
  onRemove: () => {},
  onSelect: () => {},
};

CartItems.propTypes = {
  cartItems: PropTypes.arrayOf(PropTypes.object),
  onRemove: PropTypes.func,
  onSelect: PropTypes.func,
  className: PropTypes.string,
};

export default CartItems;
